import { useCallback, useEffect, useState } from 'react';
import type { ProgresoPuesta } from '../../api/ops/negocio';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { estadoDeError } from '../../logica/estados';
import { useCabecera, type TarjetaAyuda } from '../../marco/cabecera';
import type { PropsPantalla } from '../tipos';
import './puesta.css';

/**
 * La puesta en marcha (1.7, G175, G341): «N de M pasos hechos» de `getSetupProgress` y los pasos que declaran los módulos
 * activos, en el orden que manda el servidor (G341: horario, zonas o servicios, cuenta de cobro si cobra online y página
 * de privacidad; en un negocio de citas, «Servicios y profesionales»). Cada paso lleva a la pantalla donde se completa
 * (`path`). El panel no escribe la lista de pasos: llega de la API con su título, y no se inventa ningún paso ni cifra.
 */
const AYUDA: TarjetaAyuda[] = [
    { icono: 'rocket', titulo: 'La puesta en marcha', texto: 'Los pasos para empezar a recibir reservas. Toca uno para ir a donde se completa; los hechos se marcan solos.' },
];

export function Puesta({ ops, negocio, navegar }: PropsPantalla) {
    const [progreso, setProgreso] = useState<ProgresoPuesta | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);

    useCabecera({ ayuda: AYUDA, acciones: [] });

    useEffect(() => {
        let vivo = true;
        ops.getSetupProgress(negocio)
            .then((p) => {
                if (vivo) {
                    setProgreso(p);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    const recargar = useCallback(() => {
        setProgreso(null);
        setError(null);
        setIntento((n) => n + 1);
    }, []);

    if (error !== null) {
        return <EstadoError seccion="la puesta en marcha" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} />;
    }
    if (progreso === null) {
        return <EstadoCargando seccion="la puesta en marcha" />;
    }
    const { done_count: hechos, total_count: total } = progreso;
    const porcentaje = total === 0 ? 0 : Math.round((hechos / total) * 100);
    return (
        <div className="rv-puesta">
            <section className="rv-puesta__progreso" aria-label="Progreso">
                <strong>{`${String(hechos)} de ${String(total)} pasos hechos`}</strong>
                <div
                    className="rv-puesta__barra"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={total}
                    aria-valuenow={hechos}
                    aria-label={`${String(hechos)} de ${String(total)} pasos hechos`}
                >
                    <span style={{ width: `${String(porcentaje)}%` }} />
                </div>
            </section>
            <ul className="rv-puesta__pasos">
                {progreso.steps.map((p) => (
                    <li key={`${p.module}-${p.key}`}>
                        <button
                            type="button"
                            className="rv-puesta__paso"
                            onClick={() => {
                                navegar(p.path);
                            }}
                        >
                            <span className={`rv-puesta__marca${p.done ? ' rv-puesta__marca--hecho' : ''}`}>
                                <Icono nombre={p.done ? 'check' : 'circle'} />
                            </span>
                            <span className="rv-puesta__texto">
                                <strong>{p.label}</strong>
                            </span>
                            {!p.done && <Chip estado="pending">Pendiente</Chip>}
                            <Icono nombre="chevron-right" />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
